/**
 * UserManagementAssignRolePage — mirrors Angular UserManagementAddRoleComponent (route mode).
 * Standalone page at /user-management/roles/:userId.
 *
 * Angular source: user-management-add-role.component.ts / .html
 *
 * Key Angular behaviours replicated exactly:
 *  - Liberty section hidden unless current user is ROLE_ADMIN (d-none, not removed from DOM)
 *  - Liberty roles = checkboxes (multi), all others = radio (single)
 *  - Already-assigned liberty roles → non-clickable (pointer-events:none, no opacity change)
 *  - ROLE_REFUND_APPROVAL hidden unless target user already has any liberty role
 *  - Finance Manager checkbox for COMPANY_ADMIN/COMPANY_MANAGER/PROPERTY_ADMIN/PROPERTY_MANAGER/REGIONAL_MANAGER
 *    appears only when form is otherwise valid (company + property filled where required)
 *  - Liberty + Charity sections removed for company/charity logged-in users (Angular deletes both buckets)
 *  - Renter section only for liberty users
 *  - Property dropdown multi-select when admin's own role is a company-specific role
 *  - Search triggers on even-length input only (Angular pattern)
 *  - Auto-select if API returns exactly one result
 *  - Session-filter pre-population from sessionStorage 'userlist_filter'
 *  - On success: navigate to /user-management/edit/:userId
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Loader2, Info, ShieldPlus, Shield, HelpingHand, Building2, User, AlertCircle } from 'lucide-react'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { PageHeader } from '@/components/layout/PageHeader'
import { useUserRole } from '@/hooks/useUserRole'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { SectionCard } from '@/components/ui/SectionCard'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { showSuccess, showValidationMessage, showApiError } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import { LIBERTY_ROLES, COMPANY_SPECIFIC_ROLES } from '@/constants/roles'
import { SESSION_FILTER_KEY } from '../constants'
import {
  getRolesConfig,
  getUserDetail,
  getCompanies,
  getProperties,
  getOrganizations,
  assignRole,
} from '../services/userManagementApi'
import type { AssignRoleRequest, RoleSectionProps } from '../types'
import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { buildFilteredRoles } from '../utils/buildAssignRoleBuckets'

// ─── Constants ────────────────────────────────────────────────────────────────

/** Matches Angular template condition (includes ROLE_REGIONAL_MANAGER unlike the shared constant) */
const FIN_MGR_ROLES = [
  'ROLE_COMPANY_ADMIN',
  'ROLE_COMPANY_MANAGER',
  'ROLE_PROPERTY_ADMIN',
  'ROLE_PROPERTY_MANAGER',
  'ROLE_REGIONAL_MANAGER',
] as const

const COMPANY_LEVEL = ['ROLE_COMPANY_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_REGIONAL_MANAGER']
const PROPERTY_LEVEL = ['ROLE_PROPERTY_MANAGER', 'ROLE_PROPERTY_AGENT', 'ROLE_PROPERTY_ADMIN']

// ─── Helpers ──────────────────────────────────────────────────────────────────

function needsCompany(r: string) {
  return COMPANY_LEVEL.includes(r) || PROPERTY_LEVEL.includes(r)
}
function needsProperty(r: string) {
  return PROPERTY_LEVEL.includes(r) || r === 'ROLE_REGIONAL_MANAGER'
}
function isFinMgr(r: string): boolean {
  return (FIN_MGR_ROLES as readonly string[]).includes(r)
}

// ─── Component ────────────────────────────────────────────────────────────────

export function UserManagementAssignRolePage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { roleName: myRoleName, isAdmin } = useUserRole()

  // ── Data ───────────────────────────────────────────────────────────────────
  const [pageLoading, setPageLoading] = useState(true)
  const [buckets, setBuckets] = useState<ReturnType<typeof buildFilteredRoles>>({
    liberty: [], pmc: [], charity: [], renter: [],
  })
  /** Role keys the target user already has */
  const [userRoleKeys, setUserRoleKeys] = useState<string[]>([])
  /** True if target user already has any liberty role (gates ROLE_REFUND_APPROVAL visibility) */
  const [targetHasLiberty, setTargetHasLiberty] = useState(false)

  // ── Selection ──────────────────────────────────────────────────────────────
  const [selectedRole, setSelectedRole] = useState('')       // single (PMC/Charity/Renter)
  const [libertyChecked, setLibertyChecked] = useState<string[]>([]) // multi (Liberty)
  const [roleType, setRoleType] = useState('')               // category key of selected role

  // ── Dropdowns ──────────────────────────────────────────────────────────────
  const [companyId, setCompanyId] = useState('')
  /** Single string or comma-joined for multi (matches Angular select2 multiple) */
  const [propertyId, setPropertyId] = useState<string | string[]>('')
  const [orgId, setOrgId] = useState('')
  const [companyOpts, setCompanyOpts] = useState<SearchableSelectOption[]>([])
  const [propertyOpts, setPropertyOpts] = useState<SearchableSelectOption[]>([])
  const [orgOpts, setOrgOpts] = useState<SearchableSelectOption[]>([])
  const [compLoading, setCompLoading] = useState(false)
  const [propLoading, setPropLoading] = useState(false)
  const [orgLoading, setOrgLoading] = useState(false)

  // ── Misc ───────────────────────────────────────────────────────────────────
  const [finMgr, setFinMgr] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [processing, setProcessing] = useState(false)
  const sessionFilter = useRef<Record<string, string | number> | null>(null)
  const appliedCompanyFilter = useRef(false)
  const appliedPropertyFilter = useRef(false)

  const companySeq = useRef(0)
  const propertySeq = useRef(0)
  const orgSeq = useRef(0)

  // ─── Bootstrap ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return
    try {
      const raw = sessionStorage.getItem(SESSION_FILTER_KEY)
      if (raw) sessionFilter.current = JSON.parse(raw) as Record<string, string | number>
    } catch { /* ignore */ }

    let cancelled = false
    ;(async () => {
      try {
        const [roleResult, userResult] = await Promise.allSettled([
          getRolesConfig(),
          getUserDetail(userId),
        ])
        if (cancelled) return

        if (roleResult.status === 'fulfilled') {
          setBuckets(buildFilteredRoles(roleResult.value, myRoleName))
        } else {
          showApiError(roleResult.reason)
        }

        if (userResult.status === 'fulfilled') {
          const user = userResult.value
          const keys: string[] = []
          let hasLiberty = false
          ;(user.roles ?? []).forEach((r) => {
            keys.push(r.role_name)
            if ((LIBERTY_ROLES as readonly string[]).includes(r.role_name)) hasLiberty = true
          })
          setUserRoleKeys(keys)
          setTargetHasLiberty(hasLiberty)
        } else {
          showApiError(userResult.reason)
        }
      } finally {
        if (!cancelled) setPageLoading(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  // ─── Loaders ─────────────────────────────────────────────────────────────
  const loadProperties = useCallback(async (q?: string, cid?: string) => {
    const seq = ++propertySeq.current
    const effectiveCid = cid ?? companyId
    setPropLoading(true)
    try {
      const list = await getProperties(q, effectiveCid)
      if (seq !== propertySeq.current) return
      const opts = list.map((p) => ({ value: String(p.property_id), label: p.property_name }))
      setPropertyOpts(opts)
      if (!q) {
        if (opts.length === 1) {
          setPropertyId(opts[0].value)
        } else if (!appliedPropertyFilter.current) {
          const sf = sessionFilter.current
          if (sf?.property_id && String(sf.company_id) === String(effectiveCid)) {
            setPropertyId(String(sf.property_id))
            appliedPropertyFilter.current = true
          }
        }
      }
    } catch {
      if (seq === propertySeq.current) setPropertyOpts([])
    } finally {
      if (seq === propertySeq.current) setPropLoading(false)
    }
  }, [companyId])

  const loadCompanies = useCallback(async (q?: string) => {
    const seq = ++companySeq.current
    setCompLoading(true)
    try {
      const list = await getCompanies(q)
      if (seq !== companySeq.current) return
      const opts = list.map((c) => ({ value: String(c.company_id), label: c.company_name }))
      setCompanyOpts(opts)
      if (!q) {
        if (opts.length === 1) {
          setCompanyId(opts[0].value)
          void loadProperties(undefined, opts[0].value)
        } else if (!appliedCompanyFilter.current) {
          const sf = sessionFilter.current
          if (sf?.company_id) {
            setCompanyId(String(sf.company_id))
            appliedCompanyFilter.current = true
          }
        }
      }
    } catch {
      if (seq === companySeq.current) setCompanyOpts([])
    } finally {
      if (seq === companySeq.current) setCompLoading(false)
    }
  }, [loadProperties])

  const loadOrganizations = useCallback(async (q?: string) => {
    const seq = ++orgSeq.current
    setOrgLoading(true)
    try {
      const list = await getOrganizations(q)
      if (seq !== orgSeq.current) return
      // Angular: splice out org_id === 1 (LRG) only if it is at index 0
      const listToUse = list.length > 0 && list[0].org_id === 1 ? list.slice(1) : list
      const opts = listToUse.map((o) => ({ value: String(o.org_id), label: o.org_name }))
      setOrgOpts(opts)
      if (!q && opts.length === 1) setOrgId(opts[0].value)
    } catch {
      if (seq === orgSeq.current) setOrgOpts([])
    } finally {
      if (seq === orgSeq.current) setOrgLoading(false)
    }
  }, [])

  // ─── Role selection ───────────────────────────────────────────────────────
  const handleRoleChange = useCallback((
    roleKey: string,
    catKey: string,
    inputType: 'radio' | 'checkbox',
  ) => {
    // Toggle checkbox (Liberty multi-select) or set radio (single)
    if (inputType === 'checkbox') {
      setSelectedRole('')
      setLibertyChecked((prev) =>
        prev.includes(roleKey) ? prev.filter((r) => r !== roleKey) : [...prev, roleKey],
      )
    } else {
      setLibertyChecked([])
      setSelectedRole(roleKey)
    }

    // Angular: patchValue({ company_id: '', property_id: '', org_id: '' }) + removeControls()
    setCompanyId('')
    setPropertyId('')
    setOrgId('')
    setPropertyOpts([])
    setFinMgr(false)
    setErrors({})
    setRoleType(catKey)

    // Angular: loadCompany / loadOrganization / addControls(LIBERTY)
    switch (catKey) {
      case 'COMPANY':
      case 'PROPERTY':
        void loadCompanies()
        break
      case 'ATLANTA':
        void loadOrganizations()
        break
      case 'LIBERTY':
        setOrgId('1')  // Angular: addControls('LIBERTY') pushes LRG + patchValue({org_id:'1'})
        break
    }
  }, [loadCompanies, loadOrganizations])

  // ─── Company selected → load properties ──────────────────────────────────
  const handleCompanyChange = useCallback((val: string | string[]) => {
    const cid = typeof val === 'string' ? val : val[0] ?? ''
    setCompanyId(cid)
    setPropertyId('')
    setPropertyOpts([])
    setErrors((prev) => ({ ...prev, company_id: '' }))
    if (needsProperty(selectedRole)) void loadProperties(undefined, cid)
  }, [selectedRole, loadProperties])

  // Angular: fire search on even-length input only
  const onCompanySearch = useCallback((q: string) => {
    if (q.length > 0 && q.length % 2 !== 0) return
    void loadCompanies(q)
  }, [loadCompanies])

  const onPropertySearch = useCallback((q: string) => {
    if (q.length > 0 && q.length % 2 !== 0) return
    void loadProperties(q)
  }, [loadProperties])

  const onOrgSearch = useCallback((q: string) => {
    if (q.length > 0 && q.length % 2 !== 0) return
    void loadOrganizations(q)
  }, [loadOrganizations])

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const err: Record<string, string> = {}
    if (!selectedRole && libertyChecked.length === 0) err.role = 'Please select a role.'
    if (selectedRole && needsCompany(selectedRole) && !companyId) err.company = 'Company is required.'
    const propFilled = Array.isArray(propertyId) ? propertyId.length > 0 : !!propertyId
    if (selectedRole && needsProperty(selectedRole) && !propFilled) err.property = 'Property is required.'
    if (roleType === 'ATLANTA' && !orgId) err.org = 'Organization is required.'
    setErrors(err)
    return Object.keys(err).length === 0
  }

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {
      showValidationMessage('Please Enter undefined', 'Error')
      return
    }
    setProcessing(true)
    try {
      let payload: AssignRoleRequest
      if (libertyChecked.length > 0) {
        payload = { role_name: libertyChecked, role_name_multiple: true, org_id: '1' }
      } else {
        payload = { role_name: selectedRole }
        if (companyId) payload.company_id = Number(companyId)
        if (propertyId) {
          if (Array.isArray(propertyId)) {
            payload.property_id = propertyId.map((id) => Number(id))
          } else {
            payload.property_id = Number(propertyId)
          }
        }
        if (orgId && roleType === 'ATLANTA') payload.org_id = orgId
        if (isFinMgr(selectedRole) && finMgr) payload.fin_mgr_attribute = true
      }
      const res = await assignRole(userId!, payload)
      trackApplicationEvent('user_role_assigned', 'user_management')
      const resStr = typeof res === 'string' ? res : JSON.stringify(res ?? '')
      if (resStr.includes('Invalid email')) {
        showValidationMessage(`User Role Added Successfully!<br>${resStr}`, 'Error')
      } else {
        showSuccess('User Role Added Successfully!')
      }
      navigate(`/user-management/edit/${userId}`)
    } catch (err: unknown) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  // ─── Derived flags ────────────────────────────────────────────────────────
  // Finance Manager: Angular condition — role in FIN_MGR_ROLES && form valid && control exists
  // "form valid" in React: company filled, property filled if needed
  const showFinMgr =
    !!selectedRole &&
    isFinMgr(selectedRole) &&
    needsCompany(selectedRole) &&
    !!companyId &&
    (!needsProperty(selectedRole) || (Array.isArray(propertyId) ? propertyId.length > 0 : !!propertyId))

  // Angular d-none: Liberty section hidden unless current user is ROLE_ADMIN
  const showLiberty = isAdmin && buckets.liberty.length > 0
  const showPmc = buckets.pmc.length > 0
  const showCharity = buckets.charity.length > 0
  const showRenter = buckets.renter.length > 0

  // ─── Render ───────────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-12" data-testid="um-assign-role-page">
      <PageHeader title="USER MANAGEMENT" />

      <div className="w-full px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-5">

        {/* Role selection card */}
        <SectionCard
          icon={ShieldPlus}
          title="Assign User Role"
          description="Select the role(s) to grant to this user"
        >
          {errors.role && (
            <p className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 border border-rose-200" data-testid="um-assign-role-role-error">
              {errors.role}
            </p>
          )}

          <form onSubmit={handleSubmit} noValidate data-testid="um-assign-role-form">
            {/* ── Role sections ───────────────────────────────────────────── */}
            <div className="space-y-5">

              {/* Display order matches Angular / product: Charities → Liberty → PMC → Renter */}
              {showCharity && (
                <RoleSection
                  title="Charities"
                  icon={HelpingHand}
                  roles={buckets.charity}
                  isCheckbox={false}
                  selectedSingle={selectedRole}
                  selectedMulti={libertyChecked}
                  userRoleKeys={userRoleKeys}
                  targetHasLiberty={targetHasLiberty}
                  onChange={handleRoleChange}
                />
              )}

              {/* Liberty — hidden unless ROLE_ADMIN (Angular: d-none not *ngIf) */}
              {showLiberty && (
                <RoleSection
                  title="Liberty"
                  icon={Shield}
                  roles={buckets.liberty}
                  isCheckbox
                  selectedSingle={selectedRole}
                  selectedMulti={libertyChecked}
                  userRoleKeys={userRoleKeys}
                  targetHasLiberty={targetHasLiberty}
                  onChange={handleRoleChange}
                />
              )}

              {/* PMC */}
              {showPmc && (
                <RoleSection
                  title="Property Management Companies"
                  icon={Building2}
                  roles={buckets.pmc}
                  isCheckbox={false}
                  selectedSingle={selectedRole}
                  selectedMulti={libertyChecked}
                  userRoleKeys={userRoleKeys}
                  targetHasLiberty={targetHasLiberty}
                  onChange={handleRoleChange}
                />
              )}

              {/* Renter */}
              {showRenter && (
                <RoleSection
                  title="Renter"
                  icon={User}
                  roles={buckets.renter}
                  isCheckbox={false}
                  selectedSingle={selectedRole}
                  selectedMulti={libertyChecked}
                  userRoleKeys={userRoleKeys}
                  targetHasLiberty={targetHasLiberty}
                  onChange={handleRoleChange}
                />
              )}
            </div>

            {/* ── Dropdowns (company / property / org / fin_mgr) ──────────── */}
            {(selectedRole || libertyChecked.length > 0) && (
              <div className="mt-5 pt-4 border-t border-slate-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-4 items-end">

                  {/* Company */}
                  {selectedRole && needsCompany(selectedRole) && (
                    <div className="min-w-0">
                      <label className="mb-1.5 block text-xs font-medium text-slate-500">
                        Select Company
                      </label>
                      <SearchableSelect
                        options={companyOpts}
                        value={companyId}
                        onChange={handleCompanyChange}
                        placeholder="All Company"
                        searchPlaceholder="Search By Company Name"
                        onOpen={() => void loadCompanies()}
                        onSearch={onCompanySearch}
                        loading={compLoading}
                        invokeSearchOnClose={false}
                        triggerDataTestId="um-assign-role-company-select"
                      />
                      {errors.company && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-assign-role-company-error">
                          <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                          <span>{errors.company}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Property */}
                  {selectedRole && needsProperty(selectedRole) && (
                    <div className="min-w-0">
                      <label className="mb-1.5 block text-xs font-medium text-slate-500">
                        Select Property
                      </label>
                      <SearchableSelect
                        options={propertyOpts}
                        value={Array.isArray(propertyId) ? propertyId : (propertyId || '')}
                        onChange={(val) => {
                          setPropertyId(val)
                          setErrors((p) => ({ ...p, property: '' }))
                        }}
                        placeholder="All Properties"
                        searchPlaceholder="Search By Property Name"
                        onOpen={() => { if (companyId) void loadProperties() }}
                        onSearch={onPropertySearch}
                        loading={propLoading}
                        invokeSearchOnClose={false}
                        multiple={(COMPANY_SPECIFIC_ROLES as readonly string[]).includes(myRoleName)}
                        triggerDataTestId="um-assign-role-property-select"
                      />
                      {errors.property && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-assign-role-property-error">
                          <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                          <span>{errors.property}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Organization (Charity / ATLANTA) */}
                  {roleType === 'ATLANTA' && (
                    <div className="min-w-0">
                      <label className="mb-1.5 block text-xs font-medium text-slate-500">
                        Select Organization
                      </label>
                      <SearchableSelect
                        options={orgOpts}
                        value={orgId}
                        onChange={(val) => {
                          setOrgId(typeof val === 'string' ? val : val[0] ?? '')
                          setErrors((p) => ({ ...p, org: '' }))
                        }}
                        placeholder="All Organizations"
                        searchPlaceholder="Search By Organizations Name"
                        onOpen={() => void loadOrganizations()}
                        onSearch={onOrgSearch}
                        loading={orgLoading}
                        invokeSearchOnClose={false}
                        triggerDataTestId="um-assign-role-org-select"
                      />
                      {errors.org && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-600" data-testid="um-assign-role-org-error">
                          <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                          <span>{errors.org}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Finance Manager */}
                  {showFinMgr && (
                    <div className="min-w-0 flex items-center h-[38px] pb-1">
                      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700 select-none">
                        <input
                          type="checkbox"
                          checked={finMgr}
                          onChange={(e) => setFinMgr(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)] cursor-pointer"
                          data-testid="um-assign-role-finmgr-checkbox"
                        />
                        Finance Manager
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Actions ─────────────────────────────────────────────────── */}
            <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
              <button
                type="submit"
                disabled={processing || compLoading || propLoading || orgLoading || (!addUserRoleForm_valid(selectedRole, libertyChecked, companyId, propertyId, roleType, orgId))}
                className={cn(primaryEntityActionButtonClass, 'px-6')}
                data-testid="um-assign-role-submit-btn"
              >
                {processing && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
                Submit
              </button>
              <Link
                to={`/user-management/edit/${userId}`}
                className={secondaryEntityActionButtonClass}
                data-testid="um-assign-role-cancel-link"
              >
                Cancel
              </Link>
            </div>
          </form>
        </SectionCard>
      </div>
    </div>
  )
}

/**
 * Angular submit button condition:
 * [disabled]="processing || (!addUserRoleForm.valid && !multipleRoles?.length)"
 *
 * addUserRoleForm.valid = all required controls pass.
 * Required controls: role_name (required), + company_id/property_id/org_id when present.
 */
function addUserRoleForm_valid(
  selectedRole: string,
  libertyChecked: string[],
  companyId: string,
  propertyId: string | string[],
  roleType: string,
  orgId: string,
): boolean {
  // Liberty multi-select satisfies the form
  if (libertyChecked.length > 0) return true
  // Single-select role required
  if (!selectedRole) return false
  if (needsCompany(selectedRole) && !companyId) return false
  const propOk = Array.isArray(propertyId) ? propertyId.length > 0 : !!propertyId
  if (needsProperty(selectedRole) && !propOk) return false
  if (roleType === 'ATLANTA' && !orgId) return false
  return true
}

// ─── RoleSection ─────────────────────────────────────────────────────────────

function RoleSection({
  title,
  icon: Icon,
  roles,
  isCheckbox,
  selectedSingle,
  selectedMulti,
  userRoleKeys,
  targetHasLiberty,
  onChange,
}: RoleSectionProps) {
  const inputType = isCheckbox ? 'checkbox' : 'radio'

  return (
    <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/60 to-white px-3.5 py-3.5 sm:px-4 sm:py-4 shadow-sm ring-1 ring-slate-100/50">
      <div className="mb-3 flex items-center gap-2.5 border-b border-slate-200/80 pb-2.5">
        {Icon && (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#dbeafe] border border-[#c7ddff] text-[#2563eb] shadow-sm">
            <Icon className="h-3.5 w-3.5" aria-hidden />
          </span>
        )}
        <h2 className="text-sm sm:text-base font-bold text-slate-800">
          {title}
        </h2>
      </div>
      <div className="divide-y divide-slate-100/80">
        {roles.map((role) => {
          // Angular: hide ROLE_REFUND_APPROVAL unless target user already has a liberty role
          if (role.key === 'ROLE_REFUND_APPROVAL' && !targetHasLiberty) return null

          // Angular: [ngClass]="{'non-clickable': currentRoleList.includes(role.key) && liberty_roleslist.includes(role.key)}"
          // Only liberty roles that are already assigned become non-clickable
          const alreadyAssignedLiberty =
            isCheckbox && userRoleKeys.includes(role.key)

          const isChecked = isCheckbox
            ? selectedMulti.includes(role.key)
            : selectedSingle === role.key

          return (
            <div
              key={role.key}
              className="py-0.5 first:pt-0 last:pb-0"
              data-testid={`um-assign-role-row-${role.key}`}
            >
              <label
                className={cn(
                  'flex w-full min-w-0 items-center gap-2.5 rounded-md px-1.5 py-1.5 -mx-1.5 transition-colors hover:bg-white/80',
                  alreadyAssignedLiberty ? '' : 'cursor-pointer',
                )}
              >
                <input
                  type={inputType}
                  name={inputType === 'radio' ? 'role_name' : undefined}
                  checked={isChecked}
                  disabled={alreadyAssignedLiberty}
                  onChange={() => {
                    if (alreadyAssignedLiberty) return
                    onChange(role.key, role.roleType, inputType)
                  }}
                  className={cn(
                    'h-4 w-4 shrink-0 border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]',
                    !alreadyAssignedLiberty && 'cursor-pointer',
                  )}
                  data-testid={`um-assign-role-input-${role.key}`}
                />
                {/* Name + description control stay adjacent (no justify-between) */}
                <div className="min-w-0 flex-1">
                  <div className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1.5">
                    <span className="text-sm text-slate-800 leading-snug break-words [word-break:break-word]">
                      {role.name}
                    </span>
                    {role.desc ? (
                      <div className="relative inline-flex shrink-0 items-center group/info">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/35"
                          aria-label={`What this role means: ${role.name}`}
                          data-testid={`um-assign-role-desc-trigger-${role.key}`}
                        >
                          <Info className="h-3.5 w-3.5" aria-hidden />
                        </button>
                        <div
                          className="invisible pointer-events-none absolute left-0 top-full z-20 mt-1 w-[min(15rem,calc(100vw-5rem))] max-w-[240px] sm:max-w-[260px]
                            rounded-lg border border-amber-300/90 bg-amber-50 p-3 text-xs font-normal text-amber-950 opacity-0 shadow-xl ring-1 ring-amber-200/60
                            transition-all duration-150
                            group-hover/info:visible group-hover/info:opacity-100"
                          dangerouslySetInnerHTML={{ __html: role.desc }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </label>
            </div>
          )
        })}
      </div>
    </div>
  )
}
